import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Radar, Loader2, Lock, User, Mail, Phone, MessageCircle, CheckCircle2, AlertCircle, LogOut, Sparkles, FileText, Copy } from "lucide-react";
import { API } from "@/lib/apiClient";
import { log } from "@/lib/log";

// Client axios dédié admin — envoie automatiquement le cookie de session
const adminApi = axios.create({ baseURL: API, withCredentials: true });

export default function AdminRadar() {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  const [leads, setLeads] = useState([]);
  const [reports, setReports] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [tab, setTab] = useState("leads");
  const [analyzing, setAnalyzing] = useState({});
  const [draftTheme, setDraftTheme] = useState("");
  const [draftCategory, setDraftCategory] = useState("");
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftResult, setDraftResult] = useState(null);
  const [draftError, setDraftError] = useState("");

  // Vérifie la session au chargement
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/me");
        if (alive && data?.authenticated) {
          setAuthed(true);
        }
      } catch (e) { log.warn("[radar] session non authentifiée :", e); }
      if (alive) setChecking(false);
    })();
    return () => { alive = false; };
  }, []);

  // Charge les données quand authed
  const loadData = useCallback(async () => {
    setDataLoading(true);
    try {
      const [leadsRes, reportsRes] = await Promise.all([
        adminApi.get("/leads"),
        adminApi.get("/emissaire-reports"),
      ]);
      setLeads(Array.isArray(leadsRes.data) ? leadsRes.data : []);
      setReports(Array.isArray(reportsRes.data) ? reportsRes.data : []);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthed(false);
      }
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) loadData();
  }, [authed, loadData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      await adminApi.post("/admin/login", { username, password });
      setAuthed(true);
      setPassword(""); // vide le champ mdp
    } catch (err) {
      if (err?.response?.status === 401) {
        setLoginError("Identifiants invalides.");
      } else {
        setLoginError("Erreur de connexion. Merci de réessayer.");
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await adminApi.post("/admin/logout"); } catch (e) { log.warn("[radar] logout serveur ignoré :", e); }
    setAuthed(false);
    setLeads([]);
    setReports([]);
  };

  const analyzeReport = async (reportId) => {
    setAnalyzing((s) => ({ ...s, [reportId]: true }));
    try {
      const { data } = await adminApi.post(`/ai/emissaire-report/${reportId}/analyze`);
      setReports((rs) => rs.map((r) => (r.id === reportId ? { ...r, analysis: data.analysis } : r)));
    } catch (e) { log.error("[radar] analyse IA échouée :", e); }
    finally {
      setAnalyzing((s) => ({ ...s, [reportId]: false }));
    }
  };

  const generateDraft = async (e) => {
    e?.preventDefault?.();
    if (!draftTheme.trim()) return;
    setDraftLoading(true);
    setDraftError("");
    setDraftResult(null);
    try {
      const { data } = await adminApi.post("/ai/article-draft", {
        theme: draftTheme.trim(),
        category: draftCategory.trim() || null,
      });
      if (data?.error) setDraftError(data.error);
      else setDraftResult(data);
    } catch (_) {
      setDraftError("Erreur lors de la génération.");
    } finally {
      setDraftLoading(false);
    }
  };

  const copyDraftJson = () => {
    if (!draftResult) return;
    try {
      navigator.clipboard.writeText(JSON.stringify(draftResult, null, 2));
    } catch (e) { log.warn("[radar] presse-papiers indisponible :", e); }
  };

  // -------- États d'affichage --------
  if (checking) {
    return (
      <div className="min-h-screen pt-28 pb-24 flex items-center justify-center" data-testid="admin-radar-page">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen pt-28 pb-24 flex items-center justify-center" data-testid="admin-radar-page">
        <div className="w-full max-w-md px-6">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <Radar className="text-gold" strokeWidth={1.5} />
            <h1 className="font-serif text-3xl md:text-4xl text-white">Accès Radar</h1>
          </div>
          <motion.form
            onSubmit={handleLogin}
            data-testid="admin-login-form"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="border border-white/10 bg-brand-surface/40 p-8 space-y-5"
          >
            <p className="text-xs uppercase tracking-luxe text-gold mb-2">Authentification requise</p>

            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="admin-username-input"
                type="text"
                autoComplete="username"
                placeholder="Identifiant"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full bg-brand-ink border border-white/15 focus:border-gold rounded-none text-white pl-10 pr-4 py-3 outline-none transition-colors"
              />
            </div>

            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="admin-password-input"
                type="password"
                autoComplete="current-password"
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-brand-ink border border-white/15 focus:border-gold rounded-none text-white pl-10 pr-4 py-3 outline-none transition-colors"
              />
            </div>

            {loginError && (
              <p data-testid="admin-login-error" className="text-red-400 text-sm">{loginError}</p>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loginLoading || !username || !password}
              className="w-full bg-gold text-brand-ink hover:bg-brand-gold-hover transition-colors px-8 py-3 text-xs uppercase tracking-[0.15em] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loginLoading ? <Loader2 className="animate-spin" size={16} /> : "Se connecter"}
            </button>

            <p className="text-[11px] text-white/30 text-center pt-2">
              Session sécurisée · cookie signé HTTPOnly · 8h
            </p>
          </motion.form>
        </div>
      </div>
    );
  }

  // -------- Contenu Radar (authed) --------
  return (
    <div className="min-h-screen pt-28 pb-24" data-testid="admin-radar-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Radar className="text-gold" strokeWidth={1.5} />
            <h1 className="font-serif text-4xl md:text-5xl text-white">Radar — Prospects & Émissaire</h1>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="text-xs uppercase tracking-[0.15em] text-white/50 hover:text-gold transition-colors inline-flex items-center gap-2 border border-white/15 hover:border-gold px-4 py-2"
          >
            <LogOut size={13} /> Déconnexion
          </button>
        </div>

        <div className="flex gap-2 mb-6 border-b border-white/10">
          <button
            data-testid="tab-leads"
            onClick={() => setTab("leads")}
            className={`px-4 py-3 text-xs uppercase tracking-[0.15em] font-semibold border-b-2 transition-colors ${
              tab === "leads" ? "text-gold border-gold" : "text-white/40 border-transparent hover:text-white/70"
            }`}
          >
            Prospects ({leads.length})
          </button>
          <button
            data-testid="tab-emissaire"
            onClick={() => setTab("emissaire")}
            className={`px-4 py-3 text-xs uppercase tracking-[0.15em] font-semibold border-b-2 transition-colors inline-flex items-center gap-2 ${
              tab === "emissaire" ? "text-gold border-gold" : "text-white/40 border-transparent hover:text-white/70"
            }`}
          >
            <MessageCircle size={13} /> Émissaire ({reports.length})
          </button>
          <button
            data-testid="tab-article-draft"
            onClick={() => setTab("draft")}
            className={`px-4 py-3 text-xs uppercase tracking-[0.15em] font-semibold border-b-2 transition-colors inline-flex items-center gap-2 ${
              tab === "draft" ? "text-gold border-gold" : "text-white/40 border-transparent hover:text-white/70"
            }`}
          >
            <FileText size={13} /> Brouillon IA
          </button>
        </div>

        {dataLoading && (
          <div className="flex items-center gap-2 text-white/50">
            <Loader2 className="animate-spin" size={16} /> Chargement…
          </div>
        )}

        {!dataLoading && tab === "leads" && (
          <div data-testid="admin-leads" className="border border-white/10 overflow-x-auto">
            <div className="px-6 py-4 border-b border-white/10 text-sm text-white/50">
              {leads.length} prospect(s)
            </div>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-gold uppercase tracking-[0.12em] text-xs">
                  <th className="p-4">Nom</th>
                  <th className="p-4">Contact</th>
                  <th className="p-4">Produit</th>
                  <th className="p-4">Email envoyé</th>
                  <th className="p-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-white/5 text-white/75">
                    <td className="p-4">{l.full_name}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2"><Mail size={13} className="text-gold" />{l.email}</div>
                      <div className="flex items-center gap-2 text-white/40"><Phone size={13} />{l.phone}</div>
                    </td>
                    <td className="p-4">{l.product}</td>
                    <td className="p-4">
                      <span className={l.notification_status === "sent" ? "text-green-400" : "text-white/40"}>
                        {l.notification_status || "—"}
                      </span>
                    </td>
                    <td className="p-4 text-white/40">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!dataLoading && tab === "emissaire" && (
          <div data-testid="admin-emissaire" className="space-y-4">
            <div className="text-sm text-white/50 mb-4">
              {reports.length} conversation(s) enregistrée(s)
            </div>
            {reports.length === 0 && (
              <p className="text-white/40 italic">Aucune conversation Émissaire enregistrée pour le moment.</p>
            )}
            {reports.map((r) => (
              <details key={r.id} className="border border-white/10 bg-brand-surface/30" data-testid="emissaire-row">                <summary className="cursor-pointer p-4 hover:bg-brand-surface/60 transition-colors flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    {r.outcome === "converti" ? (
                      <CheckCircle2 size={18} className="text-green-400 shrink-0" />
                    ) : (
                      <AlertCircle size={18} className="text-amber-400 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-white/90 text-sm truncate">
                        {r.problematique || "Problématique non exprimée"}
                      </div>
                      <div className="text-xs text-white/40 mt-1">
                        {(r.created_at || r.started_at || "").slice(0, 16).replace("T", " ")}
                        {" · "}{r.duration_seconds || 0}s
                        {" · "}{r.messages?.length || 0} messages
                        {r.notification_status && (
                          <span className={r.notification_status === "sent" ? "text-green-400 ml-2" : "text-white/40 ml-2"}>
                            · email {r.notification_status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 shrink-0 ${
                    r.outcome === "converti" ? "bg-green-400/10 text-green-400" : "bg-amber-400/10 text-amber-400"
                  }`}>
                    {r.outcome === "converti" ? "Converti" : "Non converti"}
                  </span>
                  {r.analysis && (
                    <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 shrink-0 border border-gold/40 text-gold ml-2">
                      IA {r.analysis.hotness}/100
                    </span>
                  )}
                </summary>
                <div className="border-t border-white/10 p-4 space-y-2">
                  {r.analysis && (
                    <div className="mb-3 p-4 border border-gold/40 bg-gold/5" data-testid="analysis-block">
                      <div className="flex items-center gap-3 mb-2">
                        <Sparkles size={14} className="text-gold" />
                        <span className="text-[11px] uppercase tracking-luxe text-gold">Analyse IA — Claude</span>
                        <span className="ml-auto font-serif text-2xl text-gold">{r.analysis.hotness}<span className="text-xs text-white/40">/100</span></span>
                      </div>
                      <ul className="text-xs text-white/70 space-y-1 mb-2">
                        {(r.analysis.reasons || []).map((rs) => <li key={rs}>• {rs}</li>)}
                      </ul>
                      {r.analysis.next_action && (
                        <p className="text-xs text-white/60 italic">→ {r.analysis.next_action}</p>
                      )}
                    </div>
                  )}
                  {!r.analysis && (
                    <button
                      data-testid={`analyze-btn-${r.id}`}
                      onClick={() => analyzeReport(r.id)}
                      disabled={analyzing[r.id]}
                      className="mb-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-gold border border-gold/40 hover:bg-gold/10 px-3 py-2 transition-colors disabled:opacity-50"
                    >
                      {analyzing[r.id] ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                      Analyser avec Claude
                    </button>
                  )}
                  {(r.messages || []).map((m, idx) => (
                    <div key={`${m.timestamp || m.ts || idx}-${m.role}`} className={`p-3 text-sm ${
                      m.role === "user"
                        ? "bg-gold/10 border-l-2 border-gold text-white/90"
                        : "bg-white/5 border-l-2 border-white/20 text-white/70"
                    }`}>
                      <div className="text-[10px] uppercase tracking-[0.15em] mb-1 text-white/40">
                        {m.role === "user" ? "Prospect" : "Émissaire"}
                      </div>
                      {m.content}
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        )}

        {!dataLoading && tab === "draft" && (
          <div data-testid="admin-article-draft" className="space-y-6">
            <div className="text-sm text-white/50">
              Générez un brouillon d'article Journal Souverain avec Claude Sonnet 5 — style Rodrigue, format JSON prêt à coller dans <code className="text-gold">/app/frontend/src/data/articles.js</code>.
            </div>
            <form onSubmit={generateDraft} className="border border-white/10 bg-brand-surface/30 p-6 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">Thème de l'article</label>
                <input
                  data-testid="draft-theme"
                  type="text"
                  value={draftTheme}
                  onChange={(e) => setDraftTheme(e.target.value)}
                  placeholder="Ex : Comprendre son nombre Kua en 5 minutes"
                  required
                  className="w-full bg-brand-ink border border-white/15 focus:border-gold rounded-none text-white px-4 py-3 outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">Catégorie (optionnelle)</label>
                <input
                  data-testid="draft-category"
                  type="text"
                  value={draftCategory}
                  onChange={(e) => setDraftCategory(e.target.value)}
                  placeholder="Ex : Feng Shui, Numérologie, Souveraineté & Cycles…"
                  className="w-full bg-brand-ink border border-white/15 focus:border-gold rounded-none text-white px-4 py-3 outline-none transition-colors"
                />
              </div>
              <button
                data-testid="draft-generate"
                type="submit"
                disabled={draftLoading || !draftTheme.trim()}
                className="inline-flex items-center gap-2 bg-gold text-brand-ink hover:bg-brand-gold-hover transition-colors px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold disabled:opacity-60"
              >
                {draftLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {draftLoading ? "Génération en cours (30-60 s)…" : "Générer avec Claude"}
              </button>
              {draftError && <p className="text-red-400 text-sm">{draftError}</p>}
            </form>

            {draftResult && (
              <div className="border border-gold/40 bg-brand-surface/30" data-testid="draft-result">
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-gold mb-1">Brouillon généré</p>
                    <p className="font-serif text-lg text-white">{draftResult.title}</p>
                    <p className="text-xs text-white/50 mt-1">{draftResult.excerpt}</p>
                  </div>
                  <button
                    onClick={copyDraftJson}
                    data-testid="draft-copy"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-gold border border-gold/40 hover:bg-gold/10 px-3 py-2 transition-colors"
                  >
                    <Copy size={12} /> Copier JSON
                  </button>
                </div>
                <pre className="p-4 text-xs text-white/70 overflow-x-auto max-h-[500px] whitespace-pre-wrap font-mono">
                  {JSON.stringify(draftResult, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
